import React, { useContext } from 'react';
import { Avatar, Box, VStack, Text, Button, useToast, IconButton, Flex, Icon } from '@chakra-ui/react';
import { FiChevronLeft, FiClock, FiFileText, FiPackage } from 'react-icons/fi';
import { StickyCtaBar } from '../../../components/ui';
import { useNavigate, useLocation } from 'react-router-dom';
import ShopifyShippingTracker from '../components/shopify/ShopifyShippingTracker';
import { collabApi } from '../../../services/api/collab';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerCampaignDataContext } from 'contexts/InfluencerCampaignDataContext';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { ensureConversationExists } from 'utils/collabUtils';
import HelpSection from '../components/HelpSection';
import { submissionPathForCollab } from '../utils/collabRoutes';
import CreativeBriefModal from '../components/CreativeBriefModal';
import { ff } from '../../../theme';
import { useProductSelectionGuard } from '../utils/useProductSelectionGuard';

const PendingShipmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');

  const collabData = useContext(CollabDataContext);
  const campaignData = useContext(InfluencerCampaignDataContext);
  const clients = useContext(ClientDataContext);

  const collab = collabData.find(c => c.id === collabId);
  const campaign = campaignData[collabId!];
  // A brand can add an optional product to a live campaign; the endpoint below refuses a collab
  // that owes a selection. Send the creator to make one instead of failing with a 400.
  const goToProductSelection = useProductSelectionGuard(collab, campaign);
  const client = clients[campaign?.clientID];

  const [isCreativeBriefOpen, setIsCreativeBriefOpen] = React.useState(false);
  const [isRedeeming, setIsRedeeming] = React.useState(false);

  const handleReadyToPost = async () => {
    if (!collab) return;
    
    setIsRedeeming(true);
    try {
      if (goToProductSelection()) return;
      await collabApi.advanceCollabToContentSubmission({
        collabId: collab.id,
        requireDraftApproval: campaign?.requireDraftApproval,
        skipRedeem: true,
      });
      
      toast({
        title: "Status updated",
        description: "You can now proceed to submit your content.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      navigate(submissionPathForCollab(collab), { replace: true });
    } catch (error) {
      console.error('Error updating collab status:', error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  if (!collab || !campaign) {
    return <Box>Loading...</Box>;
  }

  const extraOrderIds = collab.shopifyExtraShipmentOrderIDs ?? [];
  const shipCount = (collab.shopifyOrderID ? 1 : 0) + extraOrderIds.length;
  const trackerBox = { border: '1px solid', borderColor: '#E4E4E0', borderRadius: '20px', overflow: 'hidden' } as const;

  return (
    <Box minH="100svh" bg="white" color={ff.ink} fontFamily="display">
      <Box maxW={{ base: '560px', lg: '720px' }} mx="auto" pb="160px">
        <Flex align="center" gap="8px" pt="max(12px, env(safe-area-inset-top))" px="12px">
          <IconButton
            aria-label="Go back"
            icon={<FiChevronLeft size={22} strokeWidth={2.4} />}
            onClick={() => navigate(-1)}
            variant="unstyled"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex="none"
            w="44px"
            minW="44px"
            h="44px"
            borderRadius="999px"
            color={ff.ink}
          />
          <Text flex={1} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" lineHeight="normal">
            Order status
          </Text>
        </Flex>

        <VStack align="stretch" spacing="16px" px="20px" pt="8px">
          <Text as="h1" textStyle="display" fontSize="32px">
            {extraOrderIds.length ? 'More on the way' : 'Your order is on the way'}
          </Text>

          <Flex align="center" gap="12px">
            <Avatar
              src={client?.instagramProfilePicture}
              name={client?.accountName}
              boxSize="48px"
              borderRadius="12px"
              bg="#F3DCD3"
              color={ff.ink}
              flex="none"
              sx={{ '& > [role=img]': { fontSize: '15px', fontWeight: 600 }, img: { borderRadius: '12px' } }}
            />
            <VStack align="stretch" spacing="2px" flex={1} minW={0}>
              <Text fontSize="16px" fontWeight={600} lineHeight="normal" noOfLines={1}>{client?.accountName}</Text>
              <Text fontSize="14px" lineHeight="normal" color={ff.body} noOfLines={1}>{campaign?.name}</Text>
            </VStack>
            <Button
              onClick={() => setIsCreativeBriefOpen(true)}
              aria-label="View brief"
              variant="unstyled"
              display="flex"
              alignItems="center"
              gap="6px"
              flex="none"
              h="44px"
              px="14px"
              borderRadius="999px"
              border="1.5px solid"
              borderColor={ff.ink}
              bg="white"
              fontSize="14px"
              fontWeight={600}
              color={ff.ink}
            >
              <Icon as={FiFileText} boxSize="16px" />
              View brief
            </Button>
          </Flex>

          {extraOrderIds.length > 0 && (
            <Flex gap="10px" align="flex-start" p="14px" borderRadius="16px" bg={ff.limeSurface} fontSize="14px" lineHeight="1.4" color="#2A2A2C">
              <Icon as={FiPackage} boxSize="20px" flex="none" mt="1px" />
              <Text>
                <Text as="strong">{client?.accountName} sent an extra shipment.</Text> Wait for both before you confirm, so your content can feature everything.
              </Text>
            </Flex>
          )}

          <Flex gap="10px" align="flex-start" p="14px" borderRadius="16px" bg={ff.grey} fontSize="14px" lineHeight="1.4" color={ff.body}>
            <Icon as={FiClock} boxSize="20px" flex="none" mt="1px" />
            <Text>Your content clock started when your order shipped. Tap below once it arrives.</Text>
          </Flex>

          {shipCount > 0 && (
            <Text as="h2" mt="4px" fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2} lineHeight="normal">
              Shipments · {shipCount}
            </Text>
          )}

          {collab.shopifyOrderID && (
            <Box {...trackerBox}>
              <ShopifyShippingTracker
                clientId={campaign?.clientID}
                collab={collab}
                campaign={campaign}
                label={extraOrderIds.length ? 'Original order' : undefined}
              />
            </Box>
          )}

          {extraOrderIds.map((orderId) => (
            <Box key={orderId} {...trackerBox}>
              <ShopifyShippingTracker
                clientId={campaign?.clientID}
                collab={collab}
                campaign={campaign}
                orderId={orderId}
                label="Additional items"
              />
            </Box>
          ))}

          <HelpSection collab={collab} campaign={campaign} client={client} />
        </VStack>
      </Box>

      <StickyCtaBar noNav>
        <Button
          variant="lime"
          w="100%"
          h="56px"
          borderRadius="999px"
          fontSize="17px"
          fontWeight={600}
          onClick={handleReadyToPost}
          isLoading={isRedeeming}
          aria-label="I've received my order"
        >
          I've Received My Order
        </Button>
      </StickyCtaBar>

      <CreativeBriefModal
        isOpen={isCreativeBriefOpen}
        onClose={() => setIsCreativeBriefOpen(false)}
        collab={collab}
        campaign={campaign}
      />
    </Box>
  );
};

export default PendingShipmentPage;
